import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  ArrowLeft, Plus, FileCheck, AlertTriangle, CheckCircle2, Clock, 
  Trash2, Edit, DollarSign, Calendar, User, FileText, ShieldCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WAIVER_TYPES = [
  { value: 'conditional_progress', label: 'Conditional Progress', description: 'Conditional on payment clearing' },
  { value: 'unconditional_progress', label: 'Unconditional Progress', description: 'Payment already received' },
  { value: 'conditional_final', label: 'Conditional Final', description: 'Final waiver, conditional on payment' },
  { value: 'unconditional_final', label: 'Unconditional Final', description: 'Final waiver, payment received' }
];

const VENDOR_TYPES = [
  { value: 'contractor', label: 'General Contractor' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'supplier', label: 'Material Supplier' }
];

const STATUS_CONFIG = {
  requested: { label: 'Requested', color: 'bg-amber-100 text-amber-700', icon: Clock },
  received: { label: 'Received', color: 'bg-blue-100 text-blue-700', icon: FileCheck },
  verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  filed: { label: 'Filed', color: 'bg-emerald-100 text-emerald-700', icon: ShieldCheck }
};

export default function LienWaiverPage() {
  const darkMode = document.documentElement.classList.contains('dark');
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [waivers, setWaivers] = useState([]);
  const [summary, setSummary] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingWaiver, setEditingWaiver] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_type: 'contractor',
    waiver_type: 'conditional_progress',
    payment_amount: '',
    through_date: '',
    invoice_id: '',
    invoice_number: '',
    check_number: '',
    payment_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [waiversRes, alertsRes, projectRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/api/projects/${projectId}/lien-waivers`),
        axios.get(`${API}/api/projects/${projectId}/lien-waiver-alerts`),
        axios.get(`${API}/api/projects/${projectId}`),
        axios.get(`${API}/api/vendors`).catch(() => ({ data: [] }))
      ]);
      setWaivers(waiversRes.data.waivers || []);
      setSummary(waiversRes.data.summary || {});
      setAlerts(alertsRes.data.alerts || []);
      setProject(projectRes.data);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load lien waivers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.vendor_name || !formData.payment_amount || !formData.through_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        payment_amount: parseFloat(formData.payment_amount)
      };
      
      if (editingWaiver) {
        const res = await axios.put(`${API}/api/projects/${projectId}/lien-waivers/${editingWaiver.id}`, data);
        setWaivers(waivers.map(w => w.id === editingWaiver.id ? res.data.waiver : w));
        toast.success('Lien waiver updated');
      } else {
        const res = await axios.post(`${API}/api/projects/${projectId}/lien-waivers`, data);
        setWaivers([...waivers, res.data.waiver]);
        toast.success('Lien waiver request created');
      }
      setShowAddDialog(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save lien waiver');
    }
  };

  const handleDelete = async (waiverId) => {
    if (!window.confirm('Delete this lien waiver record?')) return;
    try {
      await axios.delete(`${API}/api/projects/${projectId}/lien-waivers/${waiverId}`);
      setWaivers(waivers.filter(w => w.id !== waiverId));
      toast.success('Lien waiver deleted');
    } catch (error) {
      toast.error('Failed to delete lien waiver');
    }
  };

  const handleStatusUpdate = async (waiverId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'received') {
        updateData.received_date = new Date().toISOString();
      }
      const res = await axios.put(`${API}/api/projects/${projectId}/lien-waivers/${waiverId}`, updateData);
      setWaivers(waivers.map(w => w.id === waiverId ? res.data.waiver : w));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleVerify = async (waiverId) => {
    try {
      const res = await axios.post(`${API}/api/projects/${projectId}/lien-waivers/${waiverId}/verify`, {
        verified_by: 'User'  // In production, get from auth context
      });
      setWaivers(waivers.map(w => w.id === waiverId ? res.data.waiver : w));
      toast.success('Lien waiver verified');
    } catch (error) {
      toast.error('Failed to verify lien waiver');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      vendor_name: '',
      vendor_type: 'contractor',
      waiver_type: 'conditional_progress',
      payment_amount: '',
      through_date: '',
      invoice_id: '',
      invoice_number: '',
      check_number: '',
      payment_date: '',
      notes: ''
    });
    setEditingWaiver(null);
  };

  const openEditDialog = (waiver) => {
    setEditingWaiver(waiver);
    setFormData({
      vendor_id: waiver.vendor_id || '',
      vendor_name: waiver.vendor_name || '',
      vendor_type: waiver.vendor_type || 'contractor',
      waiver_type: waiver.waiver_type || 'conditional_progress',
      payment_amount: waiver.payment_amount?.toString() || '',
      through_date: waiver.through_date?.split('T')[0] || '',
      invoice_id: waiver.invoice_id || '',
      invoice_number: waiver.invoice_number || '',
      check_number: waiver.check_number || '',
      payment_date: waiver.payment_date?.split('T')[0] || '',
      notes: waiver.notes || ''
    });
    setShowAddDialog(true);
  };

  const handleVendorSelect = (vendorId) => {
    if (vendorId === 'manual') {
      setFormData({ ...formData, vendor_id: '', vendor_name: '' });
      return;
    }
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      setFormData({
        ...formData,
        vendor_id: vendorId,
        vendor_name: vendor.name || vendor.company_name || ''
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#70170F]" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-[#F5F3EF]'}`}>
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1918]">Lien Waiver Tracking</h1>
            <p className="text-muted-foreground">{project?.name || 'Project'}</p>
          </div>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-[#70170F] hover:bg-[#8B1E14]">
            <Plus className="w-4 h-4 mr-2" />
            Request Waiver
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="max-w-6xl mx-auto mb-6">
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                Attention Required ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {alerts.slice(0, 3).map((alert, idx) => (
                <div key={idx} className={`flex items-center justify-between p-2 rounded border border-amber-200 ${darkMode ? 'bg-[#1A1A1A] border-amber-700' : 'bg-white'}`}>
                  <div>
                    <p className="text-sm font-medium">{alert.vendor_name}</p>
                    <p className="text-xs text-muted-foreground">{alert.message}</p>
                  </div>
                  <Badge variant="outline" className={alert.severity === 'high' ? 'border-red-500 text-red-600' : 'border-amber-500 text-amber-600'}>
                    {alert.severity === 'high' ? 'Missing' : 'Pending'}
                  </Badge>
                </div>
              ))}
              {alerts.length > 3 && (
                <p className="text-xs text-muted-foreground text-center">+ {alerts.length - 3} more alerts</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary Cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <FileCheck className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total_requested || 0}</p>
                <p className="text-xs text-muted-foreground">Total Requested</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{summary.total_received || 0}</p>
                <p className="text-xs text-muted-foreground">Received</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{summary.total_verified || 0}</p>
                <p className="text-xs text-muted-foreground">Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">${((summary.pending_amount || 0) / 1000).toFixed(1)}K</p>
                <p className="text-xs text-muted-foreground">Pending Amount</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Waivers List */}
      <div className="max-w-6xl mx-auto">
        {waivers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="font-medium mb-2">No Lien Waivers Tracked</h3>
              <p className="text-sm text-muted-foreground mb-4">Start tracking lien waivers from vendors and contractors</p>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#70170F] hover:bg-[#8B1E14]">
                <Plus className="w-4 h-4 mr-2" />
                Request First Waiver
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {waivers.map(waiver => {
              const statusConfig = STATUS_CONFIG[waiver.status] || STATUS_CONFIG.requested;
              const StatusIcon = statusConfig.icon;
              const waiverTypeInfo = WAIVER_TYPES.find(t => t.value === waiver.waiver_type);
              
              return (
                <Card key={waiver.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                          <StatusIcon className={`w-6 h-6 ${statusConfig.color.split(' ')[1]}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{waiver.vendor_name}</h3>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            <Badge variant="outline" className="text-xs">
                              {waiverTypeInfo?.label || waiver.waiver_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {VENDOR_TYPES.find(t => t.value === waiver.vendor_type)?.label || waiver.vendor_type}
                          </p>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm">
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              ${waiver.payment_amount?.toLocaleString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Through: {new Date(waiver.through_date).toLocaleDateString()}
                            </span>
                            {waiver.invoice_number && (
                              <span>Invoice: #{waiver.invoice_number}</span>
                            )}
                            {waiver.check_number && (
                              <span>Check: #{waiver.check_number}</span>
                            )}
                          </div>
                          {waiver.verified_date && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Verified {new Date(waiver.verified_date).toLocaleDateString()} by {waiver.verified_by}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {waiver.status === 'requested' && (
                          <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(waiver.id, 'received')}>
                            Mark Received
                          </Button>
                        )}
                        {waiver.status === 'received' && (
                          <Button size="sm" variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" onClick={() => handleVerify(waiver.id)}>
                            <ShieldCheck className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(waiver)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(waiver.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowAddDialog(open); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWaiver ? 'Edit Lien Waiver' : 'Request Lien Waiver'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Vendor Selection */}
            {vendors.length > 0 && (
              <div>
                <Label>Select Vendor</Label>
                <Select value={formData.vendor_id || 'manual'} onValueChange={handleVendorSelect}>
                  <SelectTrigger><SelectValue placeholder="Select from vendors..." /></SelectTrigger>
                  <SelectContent>
                    {/* Radix Select v2 throws on value="" — use a 'manual' sentinel */}
                    <SelectItem value="manual">Enter manually</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name || vendor.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor Name *</Label>
                <Input
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label>Vendor Type</Label>
                <Select value={formData.vendor_type} onValueChange={(v) => setFormData({...formData, vendor_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Waiver Type */}
            <div>
              <Label>Waiver Type *</Label>
              <Select value={formData.waiver_type} onValueChange={(v) => setFormData({...formData, waiver_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WAIVER_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div>{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.payment_amount}
                  onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Work Through Date *</Label>
                <Input
                  type="date"
                  value={formData.through_date}
                  onChange={(e) => setFormData({...formData, through_date: e.target.value})}
                />
              </div>
            </div>

            {/* Reference Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                />
              </div>
              <div>
                <Label>Check Number</Label>
                <Input
                  value={formData.check_number}
                  onChange={(e) => setFormData({...formData, check_number: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={formData.payment_date}
                onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#70170F] hover:bg-[#8B1E14]">
              {editingWaiver ? 'Update' : 'Request'} Waiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
